import { Input, PaginationPageDefault, Tabs } from "@components";
import { Button } from "@components/button/button-untitledui";
import { PRIORITY_SUMMARY_CARD_DEFINITIONS } from "@constants";
import {
	CheckCircle,
	ChevronDown,
	ChevronUp,
	SearchLg,
} from "@untitledui/icons";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { cx } from "@/utils/cx";
import { COPILOT_LIST_STATUS_OPTIONS, COPILOT_TAB_OPTIONS } from "../constants";
import type {
	CopilotInsightListItem,
	CopilotListStatusFilter,
	CopilotTabKey,
} from "../types";
import { formatInsightCreatedAt, insightCategoryToGroupKey } from "../utils";
import { InsightExpandedActions } from "./insight-expanded-actions";

type InsightsListProps = {
	insights: CopilotInsightListItem[];
	activeTab: CopilotTabKey;
	currentPage: number;
	totalPages: number;
	listStatusFilter: CopilotListStatusFilter;
	isLoading?: boolean;
	errorMessage?: string | null;
	onRetry?: () => void;
	onTabChange: (tab: CopilotTabKey) => void;
	onPageChange: (page: number) => void;
	onListStatusFilterChange: (value: CopilotListStatusFilter) => void;
	onSearchChange: (value: string) => void;
};

type InsightItemProps = {
	insight: CopilotInsightListItem;
	isExpanded: boolean;
	onToggleExpanded: (insight: CopilotInsightListItem) => void;
};

const getSummaryDefinition = (insight: CopilotInsightListItem) =>
	PRIORITY_SUMMARY_CARD_DEFINITIONS.find(
		(card) => card.key === insightCategoryToGroupKey(insight.category)
	);

const InsightItem = ({
	insight,
	isExpanded,
	onToggleExpanded,
}: InsightItemProps) => {
	const definition = getSummaryDefinition(insight);

	if (!definition) {
		return null;
	}

	const Icon = definition.icon;

	return (
		<div
			className={cx(
				"overflow-hidden rounded-xl border border-border-primary bg-[#F5F5F5] p-4"
			)}
		>
			<button
				aria-expanded={isExpanded}
				className="flex w-full items-center justify-between gap-4 rounded-lg text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
				onClick={() => onToggleExpanded(insight)}
				type="button"
			>
				<div className="flex min-w-0 flex-1 items-center gap-2">
					<div
						aria-hidden
						className={cx(
							"flex size-12 shrink-0 items-center justify-center p-3",
							definition.iconBackgroundClassName
						)}
					>
						<Icon className={cx("size-6 shrink-0", definition.iconClassName)} />
					</div>
					<div className="flex min-w-0 flex-1 flex-col items-start gap-1">
						<p className="flex items-center gap-2 font-semibold text-primary text-sm">
							{insight.title}
							{insight.isResolved ? (
								<>
									<CheckCircle
										aria-hidden
										className="size-[18px] text-utility-success-700"
										strokeWidth={2}
									/>
									<span className="sr-only">Resolvido</span>
								</>
							) : null}
						</p>
						<p className="text-secondary text-sm">
							{insight.subtitle}
							{insight.driverName ? ` · ${insight.driverName}` : ""}
						</p>
						<p className="text-quaternary text-xs">
							{formatInsightCreatedAt(insight.createdAt)}
						</p>
					</div>
				</div>
				<div className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-lg px-3.5 py-2.5 font-semibold text-sm text-tertiary">
					{isExpanded ? "Menos" : "Mais"}
					{isExpanded ? (
						<ChevronUp className="size-5 shrink-0" />
					) : (
						<ChevronDown className="size-5 shrink-0" />
					)}
				</div>
			</button>

			{isExpanded ? (
				<div className="mt-4 border-border-primary border-t pt-4">
					<div className="space-y-4 rounded-xl bg-bg-primary p-4">
						<div>
							<p className="font-semibold text-primary text-sm">
								Situação identificada
							</p>
							<p className="whitespace-pre-line text-secondary text-sm">
								{insight.situationIdentified}
							</p>
						</div>
						<div>
							<p className="font-semibold text-primary text-sm">
								Risco de não agir
							</p>
							<p className="text-secondary text-sm">{insight.riskOfNoAction}</p>
						</div>
						<div>
							<p className="font-semibold text-primary text-sm">
								Ação recomendada
							</p>
							<p className="text-secondary text-sm">
								{insight.recommendedAction}
							</p>
						</div>
					</div>
					<InsightExpandedActions insight={insight} />
				</div>
			) : null}
		</div>
	);
};

export const InsightsList = ({
	insights,
	activeTab,
	currentPage,
	totalPages,
	listStatusFilter,
	isLoading = false,
	errorMessage,
	onRetry,
	onTabChange,
	onPageChange,
	onListStatusFilterChange,
	onSearchChange,
}: InsightsListProps) => {
	const [expandedInsightId, setExpandedInsightId] = useState<number | null>(
		null
	);
	const [searchValue, setSearchValue] = useState("");

	const debouncedSearchChange = useMemo(
		() =>
			debounce((value: string) => {
				onSearchChange(value);
			}, 1000),
		[onSearchChange]
	);

	useEffect(
		() => () => {
			debouncedSearchChange.cancel();
		},
		[debouncedSearchChange]
	);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const normalizedValue = value.trim();

		setSearchValue(normalizedValue);
		debouncedSearchChange(normalizedValue);
	};

	const toggleExpanded = (insight: CopilotInsightListItem) => {
		if (expandedInsightId === insight.id) {
			setExpandedInsightId(null);
			return;
		}

		setExpandedInsightId(insight.id);
	};

	return (
		<div className="flex flex-col gap-6 rounded-xl border border-border-primary bg-bg-primary p-5">
			<div className="flex flex-col gap-4">
				<div className="h-7">
					<h2 className="font-semibold text-lg text-primary">
						Notificações importantes
					</h2>
				</div>

				<fieldset className="m-0 w-full min-w-0 border-0 p-0">
					<legend className="sr-only">Filtrar insights por categoria</legend>
					<div className="flex flex-wrap gap-3 border-border-secondary border-b">
						{COPILOT_TAB_OPTIONS.map((tab) => {
							const isActive = tab.key === activeTab;
							return (
								<button
									aria-pressed={isActive}
									className={cx(
										"flex h-9 shrink-0 items-center justify-center gap-2 rounded-none border-b-2 px-1 pb-3 font-semibold text-md outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
										isActive
											? "border-fg-brand-primary_alt text-brand-secondary"
											: "border-transparent text-quaternary"
									)}
									key={tab.key}
									onClick={() => {
										debouncedSearchChange.cancel();
										onTabChange(tab.key);
										onSearchChange("");
										setSearchValue("");
									}}
									type="button"
								>
									{tab.label}
								</button>
							);
						})}
					</div>
				</fieldset>
			</div>

			<div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:justify-end">
				<div className="relative w-full sm:max-w-[307px]">
					<SearchLg
						aria-hidden
						className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-fg-quaternary"
					/>
					<Input
						aria-label="Pesquisar por placa"
						className="w-full pl-9"
						onChange={handleSearchChange}
						placeholder="Digite a placa aqui..."
						value={searchValue}
					/>
				</div>
				<Tabs
					className="items-start"
					onSelectionChange={(tabId) => {
						const parsed = Number(tabId);
						if (!Number.isNaN(parsed)) {
							onListStatusFilterChange(parsed as CopilotListStatusFilter);
						}
					}}
					selectedKey={String(listStatusFilter)}
				>
					<Tabs.List
						className="h-10 w-fit flex-row gap-0 overflow-hidden rounded-lg border border-border-primary"
						items={COPILOT_LIST_STATUS_OPTIONS}
						type="button-gray"
					>
						{(tab) => (
							<Tabs.Item
								{...tab}
								className="h-full rounded-none border-border-primary not-last:border-r font-semibold aria-selected:bg-disabled"
								id={String(tab.id)}
							/>
						)}
					</Tabs.List>
				</Tabs>
			</div>

			{errorMessage ? (
				<div
					className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-primary bg-bg-secondary px-4 py-3"
					role="alert"
				>
					<p className="text-secondary text-sm">{errorMessage}</p>
					{onRetry ? (
						<Button color="secondary" onClick={onRetry} size="sm">
							Tentar novamente
						</Button>
					) : null}
				</div>
			) : null}

			{isLoading ? (
				<output className="flex flex-col gap-4">
					<span className="sr-only">Carregando insights</span>
					{[1, 2, 3].map((key) => (
						<span
							aria-hidden
							className="block h-28 animate-pulse rounded-xl bg-secondary"
							key={key}
						/>
					))}
				</output>
			) : null}

			{!(isLoading || errorMessage) && insights.length === 0 ? (
				<div className="py-10 text-center text-quaternary text-sm">
					Nenhum item encontrado para este filtro.
				</div>
			) : null}

			{!isLoading && insights.length > 0 ? (
				<div className="flex flex-col gap-4">
					{insights.map((insight) => (
						<InsightItem
							insight={insight}
							isExpanded={expandedInsightId === insight.id}
							key={insight.id}
							onToggleExpanded={toggleExpanded}
						/>
					))}
				</div>
			) : null}

			{totalPages > 1 ? (
				<PaginationPageDefault
					className="border-t border-t-border-secondary p-0! pt-4!"
					onPageChange={(page) => {
						onPageChange(Math.max(1, Math.min(page, totalPages)));
					}}
					page={currentPage}
					total={totalPages || 1}
				/>
			) : null}
		</div>
	);
};
